import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, CreditCard, Sparkles, Wallet } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import {
  billingApi,
  premiumApi,
  ApiError,
  type BillingStatus,
  type PremiumBillingMode,
} from '../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '../components/ui';
import { cn } from '../lib/cn';

type PlanKey = 'lite_s' | 'lite_l';
interface PlanDef {
  key: PlanKey;
  plan: 's' | 'l';
  price: number;
  queries: number;
  highlight?: boolean;
}
const PLANS: PlanDef[] = [
  { key: 'lite_s', plan: 's', price: 20, queries: 100 },
  { key: 'lite_l', plan: 'l', price: 50, queries: 500, highlight: true },
];

/** Rang zum Vergleich Upgrade vs. Downgrade (l > s > none). */
const planRank = (p?: string | null): number => (p === 'l' ? 2 : p === 's' ? 1 : 0);

function UsageBar({ used, limit }: { used: number; limit: number }): JSX.Element {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn(
          'h-full rounded-full transition-all',
          pct >= 100 ? 'bg-status-problem' : 'bg-primary',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Billing-Seite (E2): Plan/Trial-Stand, Checkout (Stripe) und Kundenportal. Nur Org-Admin. */
export function BillingPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';
  const [params] = useSearchParams();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [busy, setBusy] = useState<PlanKey | 'portal' | 'cancel' | null>(null);
  const [premiumBusy, setPremiumBusy] = useState<'mode' | 'setup' | null>(null);

  const fmtEur = (cents: number): string =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(
      cents / 100,
    );

  /** Premium-Abrechnungsart umstellen (nur Org-Admin; Server erzwingt es zusätzlich). */
  async function onSetPremiumMode(mode: PremiumBillingMode): Promise<void> {
    if (!isAdmin || premiumBusy) return;
    setActionErr('');
    setPremiumBusy('mode');
    try {
      setStatus(await billingApi.setPremiumMode(mode, token));
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : 'Error');
    } finally {
      setPremiumBusy(null);
    }
  }

  /** PAYG-Zahlungsweise hinterlegen → Stripe-Checkout. */
  async function onSetupPayment(): Promise<void> {
    setActionErr('');
    setPremiumBusy('setup');
    try {
      const { url } = await premiumApi.paygSetup(token);
      window.location.href = url;
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : 'Error');
      setPremiumBusy(null);
    }
  }

  const checkoutResult = params.get('status'); // 'success' | 'cancel'

  useEffect(() => {
    let cancelled = false;
    billingApi
      .status(token)
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setLoadErr(t('billing.loadError'));
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  async function onCheckout(plan: PlanKey): Promise<void> {
    setActionErr('');
    setBusy(plan);
    try {
      const { url } = await billingApi.checkout(plan, token);
      window.location.href = url;
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : 'Error');
      setBusy(null);
    }
  }

  async function onPortal(): Promise<void> {
    setActionErr('');
    setBusy('portal');
    try {
      const { url } = await billingApi.portal(token);
      window.location.href = url;
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : 'Error');
      setBusy(null);
    }
  }

  /** Plan-Wechsel: Upgrade sofort, Downgrade zum Periodenende (serverseitig entschieden). */
  async function onChangePlan(plan: PlanKey): Promise<void> {
    setActionErr('');
    setBusy(plan);
    try {
      await billingApi.changePlan(plan, token);
      setStatus(await billingApi.status(token));
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : 'Error');
    } finally {
      setBusy(null);
    }
  }

  /** Geplanten Downgrade abbrechen = auf dem aktuellen Plan bleiben. */
  async function onCancelScheduled(): Promise<void> {
    if (!status) return;
    setActionErr('');
    setBusy('cancel');
    try {
      await billingApi.changePlan(status.plan === 'l' ? 'lite_l' : 'lite_s', token);
      setStatus(await billingApi.status(token));
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : 'Error');
    } finally {
      setBusy(null);
    }
  }

  const active = status?.hasActivePlan ?? false;
  const stripeReady = status?.stripeConfigured ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('billing.title')}</h1>
        <p className="text-muted-foreground">{t('billing.subtitle')}</p>
      </div>

      {checkoutResult === 'success' && <Alert kind="info">{t('billing.successMsg')}</Alert>}
      {checkoutResult === 'cancel' && <Alert kind="warn">{t('billing.cancelMsg')}</Alert>}
      {status?.scheduledPlan && status.scheduledChangeAt && (
        <Alert kind="warn">
          {t('billing.scheduledChange', {
            plan: t(`billing.plan${status.scheduledPlan.toUpperCase()}`),
            date: new Date(status.scheduledChangeAt).toLocaleDateString(i18n.language),
          })}{' '}
          <button
            type="button"
            className="font-medium underline underline-offset-2 disabled:opacity-50"
            disabled={busy !== null}
            onClick={() => void onCancelScheduled()}
          >
            {t('billing.undoChange')}
          </button>
        </Alert>
      )}
      {loadErr && <Alert kind="error">{loadErr}</Alert>}
      {actionErr && <Alert kind="error">{actionErr}</Alert>}
      {status && !stripeReady && <Alert kind="info">{t('billing.notConfigured')}</Alert>}

      {/* Aktueller Stand: Plan + Verbrauch/Trial. */}
      {status && (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <CardTitle>{t('billing.currentPlan')}:</CardTitle>
              <Badge variant={active ? 'primary' : 'default'}>
                {active ? t(`billing.plan${status.plan.toUpperCase()}`) : t('billing.planNone')}
              </Badge>
              {active && <Badge variant="success">{t('billing.active')}</Badge>}
            </div>
            {active && status.stripeConfigured && (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy !== null}
                onClick={() => void onPortal()}
              >
                {busy === 'portal' ? <Spinner /> : <CreditCard className="h-4 w-4" />}
                {t('billing.manage')}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            {active && status.limit !== null ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('billing.usage', { used: status.used, limit: status.limit })}
                  </span>
                  <span className="font-medium">
                    {t('billing.remaining', { remaining: status.remaining })}
                  </span>
                </div>
                <UsageBar used={status.used} limit={status.limit} />
                {status.periodEnd && (
                  <p className="text-xs text-muted-foreground">
                    {t('billing.renews', {
                      date: new Date(status.periodEnd).toLocaleDateString(i18n.language),
                    })}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {status.trialRemaining > 0
                      ? t('billing.trialRemaining', {
                          remaining: status.trialRemaining,
                          quota: status.trialQuota,
                        })
                      : t('billing.trialDone')}
                  </span>
                </div>
                <UsageBar used={status.trialUsed} limit={status.trialQuota} />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Premium-Abrechnung (EPIC P): Wallet ↔ Pay-as-you-go (nur Org-Admin) + PAYG-Setup + Monatszähler. */}
      {status && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" /> {t('billing.premiumSectionTitle')}
            </CardTitle>
            <CardDescription>{t('billing.premiumMode')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isAdmin && <Alert kind="info">{t('billing.modeAdminOnly')}</Alert>}
            <div className="grid gap-3 sm:grid-cols-2">
              {(['wallet', 'payg'] as const).map((mode) => {
                const selected = (status.premiumBillingMode ?? 'wallet') === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={selected}
                    disabled={!isAdmin || premiumBusy !== null}
                    onClick={() => void onSetPremiumMode(mode)}
                    className={cn(
                      'rounded-lg border p-4 text-left transition-colors disabled:cursor-not-allowed',
                      selected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:bg-accent',
                      !isAdmin && 'opacity-80',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {mode === 'wallet' ? t('billing.modeWallet') : t('billing.modePayg')}
                      </span>
                      {selected && <Badge variant="primary">{t('billing.active')}</Badge>}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {mode === 'wallet' ? t('billing.modeWalletDesc') : t('billing.modePaygDesc')}
                    </p>
                  </button>
                );
              })}
            </div>

            {(status.premiumBillingMode ?? 'wallet') === 'payg' && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
                <span className="text-sm">
                  {status.premiumPaygOnFile ? (
                    <Badge variant="success">{t('billing.paymentOnFile')}</Badge>
                  ) : (
                    <Badge variant="warning">{t('billing.noPaymentOnFile')}</Badge>
                  )}
                </span>
                {isAdmin && !status.premiumPaygOnFile && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={premiumBusy !== null}
                    onClick={() => void onSetupPayment()}
                  >
                    {premiumBusy === 'setup' ? <Spinner /> : <CreditCard className="h-4 w-4" />}
                    {t('billing.setupPayment')}
                  </Button>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <span>
                {t('billing.premiumThisMonth', {
                  count: status.premiumThisMonth ?? 0,
                  sum: fmtEur((status.premiumThisMonth ?? 0) * 350),
                })}
              </span>
              {status.walletBalanceCents != null && (
                <span>
                  {t('billing.walletBalance', { balance: fmtEur(status.walletBalanceCents) })}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!status && !loadErr && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {/* Plan-Auswahl. */}
      <div className="grid gap-5 sm:grid-cols-2">
        {PLANS.map((p) => {
          const isCurrent = active && status?.plan === p.plan;
          const features = [
            t('billing.queriesPerMonth', { n: p.queries }),
            t('billing.feat.allCarriers'),
            t('billing.feat.statusLight'),
            p.plan === 'l' ? t('billing.feat.priority') : t('billing.feat.noStore'),
          ];
          return (
            <Card
              key={p.key}
              className={cn(p.highlight && 'border-primary/50 shadow-md', 'relative flex flex-col')}
            >
              {p.highlight && (
                <span className="absolute -top-2.5 right-4 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">
                  <Sparkles className="h-3 w-3" /> {t(`billing.plan${p.plan.toUpperCase()}`)}
                </span>
              )}
              <CardHeader>
                <CardTitle className="text-lg">
                  {t(`billing.plan${p.plan.toUpperCase()}`)}
                </CardTitle>
                <CardDescription>
                  <span className="text-3xl font-bold text-foreground">{p.price} €</span>{' '}
                  {t('billing.perSeatMonth')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4">
                <ul className="space-y-2 text-sm">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-none text-status-ok" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-2">
                  {isCurrent ? (
                    <Button variant="secondary" className="w-full" disabled>
                      {t('billing.currentBadge')}
                    </Button>
                  ) : !active ? (
                    <Button
                      className="w-full"
                      disabled={!stripeReady || busy !== null}
                      onClick={() => void onCheckout(p.key)}
                    >
                      {busy === p.key ? <Spinner /> : null}
                      {t('billing.choose')}
                    </Button>
                  ) : planRank(p.plan) > planRank(status?.plan) ? (
                    <Button
                      className="w-full"
                      disabled={!stripeReady || busy !== null}
                      onClick={() => void onChangePlan(p.key)}
                    >
                      {busy === p.key ? <Spinner /> : null}
                      {t('billing.upgradeNow')}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      className="w-full"
                      disabled={!stripeReady || busy !== null || status?.scheduledPlan === p.plan}
                      onClick={() => void onChangePlan(p.key)}
                    >
                      {busy === p.key ? <Spinner /> : null}
                      {status?.scheduledPlan === p.plan
                        ? t('billing.scheduledBadge')
                        : t('billing.switchAtPeriodEnd', {
                            date: status?.periodEnd
                              ? new Date(status.periodEnd).toLocaleDateString(i18n.language)
                              : '',
                          })}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">{t('billing.tax')}</p>
    </div>
  );
}
